import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Navbar, Footer } from '@/components/Layout';
import { CarCard } from '@/components/CarCard';
import { SearchFilters } from '@/components/SearchFilters';
import { carsApi } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CarsPage() {
  const [searchParams] = useSearchParams();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  // Track page view
  useEffect(() => {
    const trackPageView = async () => {
      try {
        let sessionId = localStorage.getItem('analytics_session');
        if (!sessionId) {
          sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('analytics_session', sessionId);
        }
        await fetch(`${API_URL}/api/analytics/track?page=cars_list&session_id=${sessionId}`, {
          method: 'POST'
        });
      } catch (error) {
        console.log('Analytics tracking failed:', error);
      }
    };
    trackPageView();
  }, []);

  useEffect(() => {
    // Load cars with URL params on mount and when params change
    const filters = Object.fromEntries(searchParams.entries());
    loadCars(filters);
  }, [searchParams]);

  const loadCars = async (filters = {}) => {
    setLoading(true);
    try {
      // Clean filters - remove 'all' values
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v && v !== 'all')
      );
      const response = await carsApi.getAll({ ...cleanFilters, is_active: true });
      setCars(response.data);
    } catch (error) {
      console.error('Error loading cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (filters) => {
    loadCars(filters);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{language === 'de' ? 'Fahrzeuge | Tim Automobile - Premium Gebrauchtwagen' : 'Vehicles | Tim Automobile - Premium Used Cars'}</title>
        <meta name="description" content={language === 'de' 
          ? 'Entdecken Sie unsere exklusive Auswahl an Premium-Fahrzeugen. Luxusautos, Sportwagen und Gebrauchtwagen von Top-Marken wie Porsche, BMW, Mercedes, Audi und mehr.' 
          : 'Discover our exclusive selection of premium vehicles. Luxury cars, sports cars and used cars from top brands like Porsche, BMW, Mercedes, Audi and more.'} />
        <meta name="keywords" content={language === 'de' ? 'Gebrauchtwagen, Premium Autos, Luxusautos, Sportwagen, Porsche, BMW, Mercedes, Audi, kaufen' : 'Used cars, Premium cars, Luxury cars, Sports cars, Porsche, BMW, Mercedes, Audi, buy'} />
        <meta property="og:title" content={language === 'de' ? 'Fahrzeuge | Tim Automobile' : 'Vehicles | Tim Automobile'} />
        <meta property="og:description" content={language === 'de' ? 'Exklusive Premium-Fahrzeuge - handverlesen und geprüft' : 'Exclusive premium vehicles - handpicked and inspected'} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://nyza-cardealer-2.preview.emergentagent.com/cars" />
      </Helmet>
      
      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <p className="overline mb-2">{t('cars_subtitle')}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light">
            {t('cars_title')}
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl">
            {t('cars_description')}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-8" data-testid="cars-filters">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <SearchFilters onFilter={handleFilter} compact />
        </div>
      </section>

      {/* Cars Grid */}
      <section className="pb-24" data-testid="cars-grid">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-muted" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-muted w-1/4" />
                    <div className="h-6 bg-muted w-1/2" />
                    <div className="h-4 bg-muted w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : cars.length === 0 ? (
            <div className="text-center py-20">
              <h3 className="font-heading text-2xl mb-2">{t('cars_no_results')}</h3>
              <p className="text-muted-foreground">{t('cars_no_results_hint')}</p>
            </div>
          ) : (
            <>
              <p className="text-muted-foreground mb-8">
                {cars.length} {cars.length === 1 ? t('cars_found_singular') : t('cars_found_plural')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {cars.map((car, index) => (
                  <CarCard key={car.id} car={car} index={index} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
